import { useCallback } from 'react';
import { toast } from 'sonner';

import { useUpdatePreseed } from '@/hooks/usePreseed';
import { useUpdateSeed } from '@/hooks/useSeeds';
import { isPreseedKey } from '@/sections/seeds/utils/preseedKey';
import { Seed } from '@/types';

/**
 * Return type for useSeedCommentHandlers hook
 */
export interface SeedCommentHandlers {
  /** Add comment/note to single or multiple seeds */
  handleAddComment: (
    seeds: Seed[],
    comment: string,
    seedType: 'domain' | 'ip' | 'webapplication'
  ) => Promise<void>;
}

/**
 * Hook for managing seed comment operations
 *
 * Provides handlers for adding notes/comments to seeds with proper error handling
 * and toast notifications.
 *
 * Features:
 * - Bulk comment support (multiple seeds at once)
 * - Single seed comment support
 * - Promise.allSettled for partial failure handling
 * - Success toast with count
 * - Type-aware seed updates (asset vs webapplication)
 *
 * @example
 * ```tsx
 * const { handleAddComment } = useSeedCommentHandlers();
 *
 * // Add comment to multiple seeds
 * await handleAddComment(selectedSeeds, 'This needs review', 'domain');
 *
 * // Add comment to single seed
 * await handleAddComment([seed], 'Approved for scanning', 'webapplication');
 * ```
 */
export const useSeedCommentHandlers = (): SeedCommentHandlers => {
  const { mutateAsync: updateSeed } = useUpdateSeed();
  const { mutateAsync: updatePreseed } = useUpdatePreseed();

  /**
   * Add comment/note to seeds
   */
  const handleAddComment = useCallback(
    async (
      seeds: Seed[],
      comment: string,
      _seedType: 'domain' | 'ip' | 'webapplication'
    ) => {
      if (seeds.length === 0) return;
      if (!comment.trim()) {
        toast.error('Comment cannot be empty');
        return;
      }

      const promises = seeds.map(seed => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const anySeed = seed as any;
        if (isPreseedKey(seed)) {
          // Preseed path: use /preseed endpoint
          return updatePreseed({
            key: anySeed.key,
            status: anySeed.status, // Keep existing status
            comment,
          });
        }

        // Regular seed path: use /seed endpoint
        return updateSeed({
          key: anySeed.key,
          identifier: anySeed.identifier,
          comment,
          showSnackbar: false,
          type: anySeed.class === 'webapplication' ? 'webapplication' : 'asset',
        });
      });

      const results = await Promise.allSettled(promises);

      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      if (succeeded > 0) {
        toast.success(
          succeeded === 1
            ? 'Note added to seed'
            : `Note added to ${succeeded} seeds`
        );
      }

      if (failed > 0) {
        toast.error(
          failed === 1
            ? 'Failed to add note to seed'
            : `Failed to add note to ${failed} seeds`
        );
      }
    },
    [updateSeed, updatePreseed]
  );

  return {
    handleAddComment,
  };
};
